export type UseCaseSummary = {
  slug: string;
  label: string;
};

export const useCasesSummary: UseCaseSummary[] = [
  { slug: "law", label: "Law firms" },
  { slug: "cybersecurity", label: "Cybersecurity companies" },
  { slug: "venture-studios", label: "Venture studios" },
  { slug: "real-estate-construction", label: "Real estate & construction" },
  { slug: "high-growth-startups", label: "High-growth startups" },
  { slug: "agencies-studios", label: "Agencies & studios" },
  { slug: "mid-market-enterprise", label: "Mid-market enterprises" },
  { slug: "b2b-saas", label: "B2B SaaS" },
  { slug: "web3", label: "Web3 projects" },
  { slug: "fitness", label: "Fitness & wellness brands" },
  { slug: "ecommerce", label: "Ecommerce & luxury brands" },
  { slug: "logistics", label: "Logistics" },
];

export type UseCase = {
  label: string;
  seoTitle?: string;
  seoDescription?: string;
  hero: {
    title: string;
    tagline: string;
    body: string[];
    cta: string;
  };
  problem: {
    heading: string;
    body: string[];
    bullets?: string[];
    closing?: string;
  };
  approach: {
    heading: string;
    body: string[];
    bullets: string[];
    closing?: string;
  };
  helps: {
    heading: string;
    items: { title: string; body: string }[];
  };
  outcomes: {
    intro: string;
    bullets: string[];
  };
  closing: {
    heading: string;
    body: string;
    cta: string;
  };
  faq?: { question: string; answer: string }[];
  caseStudySlugs?: string[];
};

const SHARED_CASE_STUDIES = [
  "how-conducttr-germany-streamlined-marketing-with-axy",
  "11x-ai-visibility-and-displaced-incumbents-in-90-days",
  "how-kuration-ai-scales-smarter-and-faster-with-axy",
];

export const useCases: Record<string, UseCase> = {
  law: {
    label: "Law firms",
    seoTitle: "AI Search Visibility for Law Firms — Get Cited by LLMs | Axy.digital",
    seoDescription:
      "Turn client questions and regulatory shifts into content that ranks on Google and gets cited by ChatGPT, Gemini, Claude & Perplexity. Get started.",
    hero: {
      title: "AI Search Visibility for Law Firms",
      tagline:
        "Become the firm clients find when they ask ChatGPT, Gemini, Google or LinkedIn who to trust — 3.4x more qualified inbound in the first 90 days.",
      body: [
        "Legal buyers are changing how they search for advice. They are asking AI tools for explanations, comparing firms online, reading thought leadership, and looking for proof of expertise before they ever book a consultation.",
        "Axy.digital helps law firms turn client questions, regulatory changes, legal trends, and market signals into timely campaigns across AI search, Google, LinkedIn, and content.",
        "Get discovered earlier. Build authority faster. Stay visible where clients are already looking.",
      ],
      cta: "Get your AI visibility report",
    },
    problem: {
      heading: "Legal marketing is becoming harder to do manually",
      body: [
        "Clients are no longer only searching “law firm near me.” They are asking questions like:",
      ],
      bullets: [
        "“What should I know before setting up a company in Singapore?”",
        "“Best law firm for startup fundraising documents”",
        "“What are the legal risks of using AI in my business?”",
        "“Which law firms specialize in cross-border founder disputes?”",
      ],
      closing:
        "These searches are happening across Google, ChatGPT, Perplexity, LinkedIn, and niche communities. But most law firms are still relying on static websites, occasional newsletters, referrals, and sporadic LinkedIn posting. By the time a firm publishes content, the conversation may have already moved on.",
    },
    approach: {
      heading: "Turn legal expertise into market visibility",
      body: [
        "Axy.digital tracks the topics your clients are actively searching for, the legal narratives gaining momentum, and the questions AI systems are already answering. Then it turns those signals into:",
      ],
      bullets: [
        "GEO-optimized articles",
        "LinkedIn thought leadership",
        "Client education content",
        "Practice-area campaigns",
        "AI search visibility reports",
        "Competitive visibility insights",
      ],
      closing:
        "So your firm can show up when clients are actively trying to understand their legal options.",
    },
    helps: {
      heading: "What Axy.digital helps law firms do",
      items: [
        { title: "Build authority around high-intent client questions", body: "Axy.digital identifies the questions your ideal clients are asking and turns them into content that positions your firm as the trusted answer." },
        { title: "Improve AI search visibility", body: "Track whether your firm is being mentioned, cited, or ignored in AI-generated recommendations and category searches." },
        { title: "Respond to regulatory shifts faster", body: "When new regulations, legal risks, or market developments emerge, Axy.digital helps you turn them into timely commentary and educational content." },
        { title: "Stay active on LinkedIn without starting from scratch", body: "Generate thought leadership based on current legal, business, and industry conversations." },
        { title: "Differentiate by practice area", body: "Create targeted campaigns for corporate law, employment law, litigation, IP, immigration, fintech, AI regulation, real estate, and other specialist areas." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, law firms can:",
      bullets: [
        "Show up earlier in the client research journey",
        "Build stronger practice-area authority",
        "Improve visibility across Google and AI search",
        "Turn legal expertise into consistent thought leadership",
        "Reduce dependency on generic SEO agencies",
        "Capture demand from emerging legal trends",
      ],
    },
    closing: {
      heading: "Become the firm clients find first",
      body: "Axy.digital helps law firms turn market demand, client questions, and regulatory change into campaigns that build trust and capture high-intent leads.",
      cta: "Get your AI visibility report",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for law firms?", answer: "It maps the questions clients actually ask — practice-area queries, regulatory concerns, cross-border questions, comparisons between firms — and turns them into GEO articles, LinkedIn thought leadership, and practice-area campaigns so your firm shows up across Google and AI search when clients are researching." },
      { question: "How is this different from hiring a legal marketing agency or freelancer?", answer: "Axy.digital runs the full loop autonomously — research, drafting, publishing, distribution — at a fraction of agency time and cost, and it keeps learning from what performs across your practice areas." },
      { question: "Does it handle practice-area landing pages and comparison content?", answer: "Yes. It generates dedicated practice-area pages, client-education explainers, and content that positions your firm against alternatives across the categories your clients search." },
      { question: "Can it handle LinkedIn thought leadership for partners, not just blog posts?", answer: "Yes, it produces founder- and partner-led LinkedIn posts turning legal expertise, case learnings, and regulatory shifts into content that builds authority beyond search." },
      { question: "How quickly do we see results?", answer: "Timing depends on your practice areas and market, but firms typically see AI-citation lift within 60–90 days and compounding inbound as content stacks up across search and LLMs." },
      { question: "Does this replace our marketing team or BD lead?", answer: "No. Axy.digital handles the operational execution so your team can focus on client relationships, positioning, and referrals instead of content ops." },
      { question: "Does it track whether we're actually being cited or recommended by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting shows whether your firm is being mentioned, cited, or ignored across ChatGPT, Gemini, Claude, and Perplexity — so you can see category presence, not just click traffic." },
    ],
  },

  cybersecurity: {
    label: "Cybersecurity companies",
    seoTitle: "AI Search Visibility for Cybersecurity — Get Cited by LLMs | Axy.digital",
    seoDescription:
      "Turn emerging threats and compliance shifts into campaigns cited by ChatGPT, Gemini, Claude & Perplexity. Publish a POV in 48h, not weeks. Start now.",
    hero: {
      title: "AI Search Visibility for Cybersecurity Companies",
      tagline:
        "Turn every new threat, exploit, or compliance shift into a published POV in 48 hours — cited by ChatGPT, Gemini, Claude, Perplexity, and Google.",
      body: [
        "Cybersecurity markets move fast. A new exploit, breach, compliance requirement, or attack vector can reshape buyer priorities overnight.",
        "Axy.digital helps cybersecurity companies detect those shifts, understand where demand is forming, and automatically turn market signals into content, campaigns, and AI search visibility.",
        "Move at the speed of the threat landscape.",
      ],
      cta: "Generate your cybersecurity marketing engine",
    },
    problem: {
      heading: "Cybersecurity buyers move when risk becomes urgent",
      body: [
        "Security buyers are overwhelmed with vendors, alerts, frameworks, and fear-based messaging. The companies that win are not always the loudest. They are the ones that show up at the exact moment buyers are trying to understand a new risk.",
        "But most cybersecurity marketing teams are stuck in slow workflows:",
      ],
      bullets: [
        "Monthly content calendars",
        "Generic threat reports",
        "Manual competitor tracking",
        "Slow approval cycles",
        "Fragmented SEO, social, and analyst-style content",
        "Campaigns that launch after the narrative has peaked",
      ],
      closing: "In cybersecurity, timing is part of relevance.",
    },
    approach: {
      heading: "Turn threat signals into demand capture",
      body: [
        "Axy.digital tracks what security buyers are searching, what competitors are publishing, what threats are trending, and what narratives are gaining traction across search, AI systems, social channels, and industry media. Then it generates campaigns around the topics your buyers already care about.",
      ],
      bullets: [
        "Threat-led thought leadership",
        "GEO and SEO articles",
        "Competitor comparison content",
        "LinkedIn campaigns",
        "Founder and expert posts",
        "AI visibility reporting",
        "Category and use-case landing page ideas",
      ],
    },
    helps: {
      heading: "What Axy.digital helps cybersecurity companies do",
      items: [
        { title: "Own emerging threat narratives", body: "When a new risk, exploit, or regulatory issue emerges, Axy.digital helps you respond with timely, relevant content." },
        { title: "Build category authority", body: "Whether you are in cloud security, identity, AppSec, compliance, endpoint, API security, or AI security, Axy.digital helps you build visibility around your category." },
        { title: "Track AI search visibility", body: "See whether AI systems are mentioning your company, competitors, or category leaders when buyers ask for recommendations." },
        { title: "Create content for technical and executive buyers", body: "Generate campaigns that speak to CISOs, security engineers, compliance teams, founders, and enterprise buyers." },
        { title: "Reduce reliance on slow content cycles", body: "Move from manual planning to signal-driven execution." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, cybersecurity companies can:",
      bullets: [
        "Capture demand around emerging threats",
        "Improve visibility in AI and search discovery",
        "Build authority in technical categories",
        "Turn security research into campaigns",
        "Respond faster to competitor narratives",
        "Support sales with timely, relevant content",
      ],
    },
    closing: {
      heading: "Stop publishing after the market has moved on",
      body: "Axy.digital helps cybersecurity companies detect market demand and turn it into campaigns before the opportunity disappears.",
      cta: "Get your AI visibility report",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for cybersecurity marketing teams?", answer: "It monitors threat signals, competitor moves, and buyer search behavior — then turns them into GEO articles, threat-led POVs, comparison pages, and LinkedIn campaigns so your company shows up when CISOs and security engineers are actually researching." },
      { question: "How is this different from hiring an agency or freelancer?", answer: "Axy.digital runs the full research → draft → publish → optimize loop autonomously and continuously — no retainer cycles, no waiting for briefs, and it keeps learning from what performs across your category." },
      { question: "Does it handle competitor comparisons and category positioning content?", answer: "Yes. It generates comparison pages, category explainers, and competitor-response campaigns whenever a rival makes a positioning move — so you're not weeks behind the narrative." },
      { question: "Can it handle LinkedIn thought leadership for CISOs and founder voices, not just blog articles?", answer: "Yes, it produces founder and expert LinkedIn posts alongside long-form content, turning threat intelligence and product insight into authority-building material." },
      { question: "How quickly do we see results?", answer: "Threat-led POVs typically ship within 48 hours of a signal, and customers see AI-citation lift and organic pipeline compound over the first 60–90 days." },
      { question: "Does this replace our marketing team?", answer: "No. Axy.digital handles the operational execution (research, drafting, publishing, optimizing) so your team can focus on positioning, analyst relations, and demand strategy." },
      { question: "Does it track whether we're actually being cited or recommended by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting shows whether your product is being mentioned, cited, or ignored across ChatGPT, Gemini, Claude, and Perplexity when buyers ask for category recommendations." },
    ],
  },

  "venture-studios": {
    label: "Venture studios",
    seoTitle: "AI GTM Infrastructure for Venture Studios — Cited by LLMs | Axy.digital",
    seoDescription:
      "Give every portfolio company an always-on GEO and AI-search engine — cut marketing setup time by 60% and validate demand faster. Start now.",
    hero: {
      title: "AI GTM Infrastructure for Venture Studios",
      tagline:
        "Give every portfolio company an always-on marketing engine — 60% less setup time per new company, cited by ChatGPT, Gemini, Claude, and Perplexity.",
      body: [
        "Venture studios do not just need marketing for one company. They need repeatable go-to-market infrastructure across a portfolio.",
        "Axy.digital helps venture studios give each company an always-on marketing engine that detects market demand, generates campaigns, improves AI search visibility, and compounds learning over time.",
        "One GTM system. Multiple companies. Faster market validation.",
      ],
      cta: "Build your portfolio GTM engine",
    },
    problem: {
      heading: "Portfolio companies need GTM before they can afford GTM teams",
      body: [
        "Most early-stage companies need positioning, content, search visibility, social presence, competitor monitoring, and market insights before they are ready to hire a marketing team. That leaves venture studios with a difficult tradeoff:",
      ],
      bullets: [
        "Hire expensive central GTM support",
        "Rely on founders to do marketing manually",
        "Outsource to agencies",
        "Let portfolio companies go to market inconsistently",
        "Wait too long to validate demand",
      ],
      closing: "The result is fragmented execution across the portfolio.",
    },
    approach: {
      heading: "A repeatable marketing engine for every company",
      body: [
        "Axy.digital gives each portfolio company a structured marketing system that understands its market, tracks demand signals, and generates campaigns automatically. For venture studios, this creates a repeatable GTM layer across the portfolio. Each company can have its own:",
      ],
      bullets: [
        "Market intelligence dashboard",
        "AI visibility report",
        "Content engine",
        "LinkedIn and X campaign system",
        "GEO and SEO strategy",
        "Competitor tracking",
        "Founder-led thought leadership workflow",
      ],
    },
    helps: {
      heading: "What Axy.digital helps venture studios do",
      items: [
        { title: "Launch portfolio companies faster", body: "Move from idea to market presence with automated messaging, content, and demand capture." },
        { title: "Validate demand earlier", body: "Use search trends, AI prompts, social narratives, and competitor signals to understand where market pull is forming." },
        { title: "Standardize GTM across the portfolio", body: "Give each company the same operating system for signal detection, strategy, and execution." },
        { title: "Reduce dependency on agencies", body: "Support more companies without multiplying retainers, freelancers, or central team workload." },
        { title: "Help founders show up consistently", body: "Turn founder expertise into thought leadership without requiring them to manage content from scratch." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, venture studios can:",
      bullets: [
        "Give every company a GTM foundation",
        "Reduce time to market",
        "Improve portfolio-wide visibility",
        "Capture demand earlier",
        "Standardize marketing operations",
        "Support more companies with the same team",
      ],
    },
    closing: {
      heading: "Give every portfolio company an unfair GTM advantage",
      body: "Axy.digital helps venture studios turn market intelligence into repeatable, always-on marketing execution across the portfolio.",
      cta: "Build your portfolio GTM engine",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for venture studios?", answer: "It gives each portfolio company an always-on marketing engine — market intelligence, GEO articles, LinkedIn campaigns, AI visibility tracking — from a single system, so every company gets the same GTM foundation without hiring per-company marketers." },
      { question: "How is this different from a central GTM team, an agency, or a freelancer?", answer: "Axy.digital replaces the operational content and research work across your whole portfolio autonomously. It's one engine, many companies — versus paying per-retainer or scaling headcount linearly." },
      { question: "Does it handle competitor comparisons and category positioning per portfolio company?", answer: "Yes. Each company gets its own competitor tracking, comparison pages, and positioning content so portfolio companies compete in their own categories, not with generic templates." },
      { question: "Can it handle founder-led content and LinkedIn thought leadership for portfolio founders?", answer: "Yes. It turns each founder's expertise, product insights, and category POV into consistent LinkedIn thought leadership without requiring them to write from scratch." },
      { question: "How quickly do we see results across portfolio companies?", answer: "New companies typically launch a full marketing engine in days rather than weeks — 60% faster setup — with AI-citation and organic traffic compounding over the first 90 days." },
      { question: "Does this replace our portfolio marketing lead?", answer: "No. Axy.digital handles the operational execution across companies so a central lead can focus on strategy, brand, and cross-portfolio patterns instead of content ops." },
      { question: "Does it track whether portfolio companies are being cited by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting per company shows citations and mentions across ChatGPT, Gemini, Claude, and Perplexity — so you can benchmark discovery presence across the portfolio." },
    ],
  },

  "real-estate-construction": {
    label: "Real estate & construction",
    seoTitle: "AI Search Visibility for Real Estate & Construction | Axy.digital",
    seoDescription:
      "Capture local high-intent demand and get cited by ChatGPT, Gemini, Claude & Google for property and construction searches. +41% qualified showings. Start now.",
    hero: {
      title: "AI Search Visibility for Real Estate & Construction",
      tagline:
        "Show up when buyers, investors, and clients ask Google, ChatGPT, Gemini, or Claude for local recommendations — +41% qualified showings in 90 days.",
      body: [
        "People are changing how they find builders, developers, agents, architects, and property specialists. They are searching locally, asking AI tools for recommendations, comparing projects, and checking credibility before making contact.",
        "Axy.digital helps real estate and construction companies detect demand signals, create localized campaigns, and show up where buyers, investors, and clients are already looking.",
        "Be found before the shortlist is made.",
      ],
      cta: "Get your local AI visibility report",
    },
    problem: {
      heading: "Local demand is moving across more channels",
      body: [
        "Real estate and construction decisions are high-trust, high-value, and often location-specific. Potential clients are asking:",
      ],
      bullets: [
        "“Best construction company in Lisbon”",
        "“Luxury renovation contractor near me”",
        "“Top real estate developer in Dubai”",
        "“How much does a home renovation cost in London?”",
        "“Best property management company for short-term rentals”",
      ],
      closing:
        "But most companies rely on referrals, outdated SEO, sporadic social posting, or project galleries that are not built for modern search behavior. If you are not visible in search, AI answers, and local content, you may be invisible before the buyer even reaches out.",
    },
    approach: {
      heading: "Turn local market demand into campaigns",
      body: [
        "Axy.digital tracks local search demand, property trends, competitor visibility, customer questions, and regional market narratives. Then it turns those signals into:",
      ],
      bullets: [
        "Local SEO and GEO articles",
        "Project-led content",
        "LinkedIn campaigns",
        "Buyer education content",
        "Location-specific landing page ideas",
        "AI search visibility reports",
        "Competitor visibility insights",
      ],
    },
    helps: {
      heading: "What Axy.digital helps real estate and construction companies do",
      items: [
        { title: "Capture local search demand", body: "Create content around the locations, project types, and services clients are already searching for." },
        { title: "Show up in AI recommendations", body: "Track whether your company appears when people ask AI tools for local recommendations." },
        { title: "Build trust before the first call", body: "Turn project experience, market knowledge, and client questions into credibility-building content." },
        { title: "Respond to market shifts", body: "Use changes in buyer demand, interest rates, regulation, renovation trends, and local property activity as campaign triggers." },
        { title: "Support sales with better content", body: "Create articles, posts, and campaigns that answer common objections before prospects speak to your team." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, real estate and construction companies can:",
      bullets: [
        "Improve local visibility",
        "Capture high-intent service demand",
        "Build trust with buyers and property owners",
        "Generate timely market commentary",
        "Differentiate from local competitors",
        "Reduce dependency on referrals alone",
      ],
    },
    closing: {
      heading: "Own the search before buyers reach out",
      body: "Axy.digital helps real estate and construction companies turn local demand into campaigns that build visibility, trust, and pipeline.",
      cta: "Get your local AI visibility report",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for real estate and construction companies?", answer: "It maps the local, high-intent searches your buyers and clients are running — 'best builder in X,' 'top developer in Y,' renovation-cost queries — and turns them into location-specific SEO/GEO articles, project pages, and LinkedIn campaigns." },
      { question: "How is this different from hiring a local SEO agency or freelancer?", answer: "Axy.digital runs the full loop autonomously and continuously across every location and service line, at a fraction of agency time and cost, and keeps learning from what actually converts." },
      { question: "Does it handle competitor comparisons and location-specific landing pages?", answer: "Yes. It generates location- and project-type-specific landing pages plus content that positions you against local alternatives buyers are already evaluating." },
      { question: "Can it handle LinkedIn and market-commentary content, not just blog articles?", answer: "Yes. It produces founder-led and executive LinkedIn posts on market conditions, project learnings, and regulatory shifts to build trust before buyers ever call." },
      { question: "How quickly do we see results?", answer: "Local visibility typically lifts within 60–90 days as content stacks up across search and AI answers, with qualified inbound compounding from there." },
      { question: "Does this replace our marketing or sales team?", answer: "No. Axy.digital handles content ops so your team can focus on client relationships, site visits, and closing — not writing service pages." },
      { question: "Does it track whether we're cited or recommended by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting shows whether your company is cited when buyers ask ChatGPT, Gemini, Claude, or Perplexity for local recommendations." },
    ],
  },

  "high-growth-startups": {
    label: "High-growth startups",
    seoTitle: "AI Marketing Engine for High-Growth Startups | Axy.digital",
    seoDescription:
      "Replace a full marketing hire with an AI engine that captures search + AI demand, gets you cited by LLMs, and ships content while you build. Start now.",
    hero: {
      title: "AI Marketing Engine for High-Growth Startups",
      tagline:
        "Replace a full marketing FTE with an always-on GEO, SEO, and AI-search engine — cited by ChatGPT, Gemini, Claude, and Perplexity from day one.",
      body: [
        "High-growth startups need to move fast. But marketing usually becomes a bottleneck: too many channels, too little time, too many tools, and not enough context.",
        "Axy.digital gives startups an algorithmic marketing engine that detects demand, turns market signals into strategy, generates campaigns, and learns from performance.",
        "Go from founder-led chaos to always-on GTM.",
      ],
      cta: "Set up your marketing engine",
    },
    problem: {
      heading: "Founders cannot keep carrying marketing manually",
      body: ["Early-stage teams need marketing before they can afford a marketing team. Founders are expected to:"],
      bullets: [
        "Build the product",
        "Talk to customers",
        "Fundraise",
        "Sell",
        "Post on LinkedIn",
        "Write blogs",
        "Track competitors",
        "Understand search demand",
        "Build category authority",
        "Generate pipeline",
      ],
      closing:
        "Most startups either under-market, hire too early, or outsource to agencies that do not understand the business deeply enough. Axy.digital was built for this gap.",
    },
    approach: {
      heading: "A GTM engine that learns your market",
      body: [
        "Axy.digital learns your company, product, ICP, positioning, competitors, and market context. Then it continuously detects signals and turns them into campaigns across:",
      ],
      bullets: [
        "SEO",
        "GEO",
        "LinkedIn",
        "X",
        "Blogs",
        "Founder thought leadership",
        "Competitor response content",
        "Product-led education",
        "AI search visibility",
      ],
      closing:
        "Instead of asking “what should we post this week?”, Axy.digital helps you act on what your market already cares about.",
    },
    helps: {
      heading: "What Axy.digital helps startups do",
      items: [
        { title: "Build consistent founder-led marketing", body: "Turn founder opinions, product insights, and market signals into regular thought leadership." },
        { title: "Capture organic demand", body: "Create content around what buyers are searching, asking, comparing, and evaluating." },
        { title: "Improve AI search visibility", body: "Track whether your startup appears in ChatGPT, Perplexity, Gemini, and other AI discovery journeys." },
        { title: "Move faster than competitors", body: "Respond to market shifts, launches, funding news, regulation, and category narratives while they are still active." },
        { title: "Reduce marketing overhead", body: "Replace scattered tools, blank-page prompting, and slow agency workflows with one execution loop." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, high-growth startups can:",
      bullets: [
        "Build market presence faster",
        "Publish consistently without manual content ops",
        "Capture search and AI discovery demand",
        "Support founder-led sales",
        "Reduce agency dependency",
        "Turn market signals into pipeline",
      ],
    },
    closing: {
      heading: "Build the marketing system your startup needs before you hire the team",
      body: "Axy.digital helps high-growth startups move faster, show up earlier, and capture demand while it is still active.",
      cta: "Set up your marketing engine",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for early-stage startups?", answer: "It replaces the first marketing hire with an always-on engine — market intelligence, GEO/SEO content, LinkedIn thought leadership, competitor response — so founders don't have to choose between building product and building pipeline." },
      { question: "How is this different from hiring an agency or freelancer?", answer: "Axy.digital runs the full research → strategy → drafting → publishing → optimization loop autonomously, learns your ICP and positioning, and delivers at a fraction of agency cost with no retainers." },
      { question: "Does it handle competitor comparisons and category-positioning content?", answer: "Yes. It generates comparison and alternative pages against your competitors and ships response content when rivals move — so you're never weeks behind." },
      { question: "Can it handle founder-led LinkedIn and X content, not just SEO blogs?", answer: "Yes. It turns founder opinions, launch notes, and product learnings into consistent LinkedIn and X posts alongside long-form content." },
      { question: "How quickly do we see results?", answer: "Startups typically see AI-citation lift and organic traffic compound within the first 60–90 days as content stacks across search and LLMs." },
      { question: "Does this replace our future marketing team?", answer: "No — it gives you the operational engine now, so when you do hire, they lead strategy and brand instead of grinding on content ops." },
      { question: "Does it track whether we're cited or recommended by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting shows whether your startup is mentioned or cited across ChatGPT, Gemini, Claude, and Perplexity for the queries your buyers actually run." },
    ],
  },

  "agencies-studios": {
    label: "Agencies & studios",
    seoTitle: "AI Marketing Delivery Stack for Agencies & Studios | Axy.digital",
    seoDescription:
      "Deliver GEO, AI search visibility, and content at scale for every client — 3.1x margin uplift vs. retainers. White-labelable engine. Get started.",
    hero: {
      title: "AI Marketing Delivery Stack for Agencies & Studios",
      tagline:
        "Deliver GEO, AI search visibility, and content at scale for every client — 3.1x margin uplift vs. the retainer model, without adding headcount.",
      body: [
        "Clients want faster insights, more content, better visibility, and clearer proof of value. But delivering all of that manually destroys margins.",
        "Axy.digital helps agencies and studios productize AI-powered marketing services across market research, AI search visibility, SEO, social, and campaign execution.",
        "Deliver more value. Protect your margins. Move faster for every client.",
      ],
      cta: "Build your AI marketing delivery stack",
    },
    problem: {
      heading: "Client expectations are rising, but agency workflows are still manual",
      body: ["Agencies are under pressure from every side. Clients want:"],
      bullets: [
        "Faster turnaround",
        "More strategic insights",
        "Better content",
        "AI search visibility",
        "Competitive intelligence",
        "Clearer reporting",
        "Lower retainers",
      ],
      closing:
        "At the same time, teams are juggling research tools, content tools, social tools, reporting tools, and custom automations that break too easily. Axy.digital gives agencies a repeatable system for delivering AI-native marketing services without rebuilding workflows from scratch.",
    },
    approach: {
      heading: "Productize your marketing delivery",
      body: [
        "Axy.digital helps agencies and studios run a signal-driven marketing loop for each client. For every client, you can generate:",
      ],
      bullets: [
        "Market intelligence reports",
        "AI visibility reports",
        "GEO content plans",
        "SEO articles",
        "LinkedIn and X campaigns",
        "Competitor insights",
        "Search demand maps",
        "Strategy recommendations",
        "Ready-to-publish content",
      ],
      closing: "This lets you move from custom manual work to repeatable, scalable delivery.",
    },
    helps: {
      heading: "What Axy.digital helps agencies and studios do",
      items: [
        { title: "Add GEO and AI visibility services", body: "Offer clients visibility into how they appear across ChatGPT, Perplexity, Gemini, and other AI discovery channels." },
        { title: "Deliver strategy faster", body: "Use market signals, competitor movement, and search demand to generate data-backed recommendations." },
        { title: "Scale content production without lowering quality", body: "Create relevant, timely content based on live demand instead of generic prompts." },
        { title: "Improve margins", body: "Reduce manual research, planning, and content operations while expanding client deliverables." },
        { title: "Support multiple clients from one system", body: "Run distinct strategy, content, and visibility workflows for every client." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, agencies and studios can:",
      bullets: [
        "Launch new AI marketing services",
        "Increase client output without increasing headcount",
        "Improve delivery speed",
        "Protect margins",
        "Replace brittle AI workflows",
        "Differentiate with market intelligence and GEO",
      ],
    },
    closing: {
      heading: "Build the AI marketing stack your clients already expect",
      body: "Axy.digital helps agencies and studios deliver signal-driven strategy and execution across every client account.",
      cta: "Build your AI marketing delivery stack",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for agencies and studios?", answer: "It gives you an AI marketing delivery engine you run across every client — market intelligence, GEO content, AI visibility reports, comparison pages, LinkedIn campaigns — so you can productize services instead of rebuilding workflows per account." },
      { question: "How is this different from hiring more freelancers or in-house content ops?", answer: "Axy.digital replaces the operational grind (research, drafting, publishing, tracking) autonomously across all client accounts, lifting margins 3x vs. the retainer-plus-freelancer model." },
      { question: "Does it handle competitor comparisons and category positioning per client?", answer: "Yes. Each client gets their own competitor tracking, comparison content, and category positioning — so you can deliver strategic outputs, not just deliverables." },
      { question: "Can it handle client LinkedIn and founder thought leadership, not just blog articles?", answer: "Yes. It generates LinkedIn thought leadership and founder-led content per client alongside long-form SEO/GEO articles." },
      { question: "How quickly do we see results for clients?", answer: "Clients typically see AI-citation lift and organic traffic compound within the first 60–90 days, and your delivery time per campaign drops immediately." },
      { question: "Does this replace our strategists or account team?", answer: "No. Axy.digital handles the operational execution so your team can focus on client strategy, positioning, and relationships instead of content production." },
      { question: "Does it track whether client brands are cited by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting per client shows citations and mentions across ChatGPT, Gemini, Claude, and Perplexity — a report you can hand to clients as proof of value." },
    ],
  },

  "mid-market-enterprise": {
    label: "Mid-market enterprises",
    seoTitle: "AI Marketing for Mid-Market Enterprises — Cited by LLMs | Axy.digital",
    seoDescription:
      "Unify market intelligence and content into one AI engine — get cited by ChatGPT, Gemini, Claude & Perplexity across business units. Get started.",
    hero: {
      title: "AI Marketing for Mid-Market Enterprises",
      tagline:
        "Unify scattered signals into coordinated campaigns cited by ChatGPT, Gemini, Claude & Perplexity — +26% share of voice vs. your peer set.",
      body: [
        "Mid-market marketing teams are drowning in data but still struggling to act quickly.",
        "Axy.digital connects market intelligence, campaign planning, content generation, distribution, and performance learning into one algorithmic marketing engine.",
        "Less fragmentation. Faster execution. Smarter campaigns.",
      ],
      cta: "See how Axy.digital works for your team",
    },
    problem: {
      heading: "Marketing teams have more data than ever, but less time to act on it",
      body: ["Mid-market companies often have the ingredients for great marketing:"],
      bullets: [
        "Customer data",
        "Sales insights",
        "Market reports",
        "Competitive intelligence",
        "Product expertise",
        "Regional knowledge",
        "Content archives",
        "Channel analytics",
      ],
      closing:
        "But that information is scattered across teams, tools, docs, dashboards, and agencies. The result is slow execution, inconsistent messaging, duplicated work, and missed market opportunities.",
    },
    approach: {
      heading: "Connect intelligence to execution",
      body: [
        "Axy.digital helps marketing teams detect market signals and turn them into coordinated campaigns. Instead of separating research, strategy, content, and performance into disconnected workflows, Axy.digital creates a closed loop:",
      ],
      bullets: ["Signal → Strategy → Content → Distribution → Feedback"],
      closing: "This gives teams a faster way to move from market change to campaign execution.",
    },
    helps: {
      heading: "What Axy.digital helps mid-market teams do",
      items: [
        { title: "Unify market intelligence", body: "Bring together search trends, AI visibility, competitor movement, social narratives, and industry signals." },
        { title: "Accelerate campaign planning", body: "Turn market insights into campaign recommendations, content plans, and channel-specific assets." },
        { title: "Improve regional and vertical marketing", body: "Adapt campaigns by market, region, segment, or buyer persona without starting from scratch." },
        { title: "Reduce agency dependency", body: "Use Axy.digital to support internal execution, brief agencies better, or replace parts of outsourced content production." },
        { title: "Learn from performance", body: "Feed engagement, visibility, and search data back into future campaign recommendations." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, mid-market enterprises can:",
      bullets: [
        "Speed up campaign cycles",
        "Improve message consistency",
        "Act faster on market opportunities",
        "Support distributed teams",
        "Reduce content and research overhead",
        "Improve visibility across search, AI, and social",
      ],
    },
    closing: {
      heading: "Turn market intelligence into execution",
      body: "Axy.digital helps mid-market teams move from scattered insights to coordinated, always-on marketing.",
      cta: "See how Axy.digital works for your team",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for mid-market marketing teams?", answer: "It unifies market intelligence, competitor tracking, GEO/SEO content production, LinkedIn campaigns, and AI visibility reporting into one engine — so your team moves from scattered dashboards to coordinated, always-on execution." },
      { question: "How is this different from hiring an agency or adding another tool?", answer: "Axy.digital replaces the operational stack (research + planning + drafting + publishing + reporting) with one autonomous engine, versus stitching together agencies, freelancers, and point tools that don't share context." },
      { question: "Does it handle competitor comparisons and category positioning across business units?", answer: "Yes. Each business unit or region can run its own competitor tracking and positioning content while sharing intelligence across the org." },
      { question: "Can it handle regional and multi-brand LinkedIn content, not just blog articles?", answer: "Yes. It generates LinkedIn thought leadership per region, product line, or executive alongside long-form SEO/GEO content." },
      { question: "How quickly do we see results?", answer: "Coordinated campaign cycles typically speed up immediately, with AI-citation and share-of-voice lift compounding over the first 60–90 days." },
      { question: "Does this replace our in-house marketing team?", answer: "No. Axy.digital handles the operational execution so your team can focus on strategy, positioning, and cross-functional partnerships." },
      { question: "Does it track whether we're cited or recommended by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting shows citations and mentions across ChatGPT, Gemini, Claude, and Perplexity — broken down by product line, region, or business unit." },
    ],
  },

  "b2b-saas": {
    label: "B2B SaaS",
    seoTitle: "AI Search Visibility for B2B SaaS — Get Cited by LLMs | Axy.digital",
    seoDescription:
      "Capture category demand and get cited by ChatGPT, Gemini, Claude, Perplexity & Google. AI native marketing and GEO built for B2B SaaS teams.",
    hero: {
      title: "AI Search Visibility for B2B SaaS",
      tagline:
        "Capture category demand, get cited by ChatGPT, Gemini, Claude, Perplexity & Google. Turn search demand and buyer intent into pipeline, automatically.",
      body: [
        "B2B buyers are researching before they ever speak to sales. They compare vendors, ask AI tools for recommendations, read category content, and search for alternatives long before they book a demo.",
        "Axy.digital helps SaaS companies show up earlier, publish faster, and capture demand across search, AI discovery, LinkedIn, and category conversations.",
        "Own the questions buyers ask before they enter your funnel.",
      ],
      cta: "Get your AI visibility report",
    },
    problem: {
      heading: "B2B SaaS discovery is no longer linear",
      body: ["Your buyers are not just searching your category keywords. They are asking:"],
      bullets: [
        "“Best software for…”",
        "“Alternatives to…”",
        "“Which tool should I use for…”",
        "“How do I solve…”",
        "“What is the difference between X and Y?”",
        "“Top platforms for…”",
      ],
      closing:
        "These questions are being answered by Google, ChatGPT, Perplexity, review sites, influencers, LinkedIn posts, and competitor content. If your company is not part of those answers, buyers may never reach your website.",
    },
    approach: {
      heading: "Turn buyer intent into campaigns",
      body: [
        "Axy.digital tracks category demand, competitor visibility, search trends, AI prompts, and social narratives. Then it generates campaigns designed to help your SaaS company show up across the buyer journey.",
      ],
      bullets: [
        "GEO articles",
        "SEO content",
        "Comparison content",
        "Use-case pages",
        "LinkedIn thought leadership",
        "Founder posts",
        "Competitor response campaigns",
        "AI visibility reporting",
      ],
    },
    helps: {
      heading: "What Axy.digital helps B2B SaaS companies do",
      items: [
        { title: "Capture category demand", body: "Create content around the exact problems, alternatives, and buying questions your audience is searching." },
        { title: "Improve AI search visibility", body: "Track whether your product is being recommended, cited, or ignored by AI systems." },
        { title: "Support sales with better content", body: "Generate assets that answer objections, explain use cases, and position your product against alternatives." },
        { title: "Build thought leadership", body: "Turn product insights, market changes, and customer pain points into content that builds authority." },
        { title: "Respond to competitors faster", body: "Detect competitor movement and create timely campaigns around positioning gaps." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, B2B SaaS teams can:",
      bullets: [
        "Improve organic discovery",
        "Capture high-intent search and AI demand",
        "Generate more relevant content",
        "Support sales enablement",
        "Build category authority",
        "Reduce dependency on manual content planning",
      ],
    },
    closing: {
      heading: "Show up where your buyers are already deciding",
      body: "Axy.digital helps B2B SaaS companies capture demand across search, AI discovery, and social channels.",
      cta: "Get your AI visibility report",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for B2B SaaS marketing teams?", answer: "It maps search demand, the \"best tool for,\" \"alternatives to,\" and comparison questions your buyers are asking, and turns it into GEO articles, SEO content, comparison pages, and LinkedIn thought leadership, so your product shows up across the full buyer journey, not just branded search." },
      { question: "How is this different from hiring a SEO/GEO agency or freelancer?", answer: "Axy.digital runs the full workflow autonomously — research, strategy, writing, publishing, and distribution — at a fraction of agency time and cost, and it keeps learning from what performs." },
      { question: "Does it create comparison and alternative pages against our competitors?", answer: "Yes. Axy.digital generates comparison content and can launch competitor response campaigns when a rival makes a positioning move, so you're not always reacting weeks later." },
      { question: "Can it handle founder-led content and LinkedIn thought leadership, not just blog articles?", answer: "Yes, the engine also produces LinkedIn thought leadership and founder posts, turning product insight and market shifts into content that builds authority, not just search rankings." },
      { question: "How quickly do we see results?", answer: "Results scale with your market, but SaaS customers have seen a 20% lift in qualified pipeline driven by LLM traffic, and our Wingbits case study reached 11x AI visibility within 90 days." },
      { question: "Does this replace our marketing team?", answer: "No, Axy.digital handles the operational execution (research, drafting, publishing, optimizing) so your team can focus on positioning, relationships, and strategy instead of content ops." },
      { question: "Does it track whether we're actually being cited or recommended by ChatGPT and other AI tools?", answer: "Yes, AI visibility reporting shows whether your product is being mentioned, cited, or ignored across AI systems, so you can see category presence, not just click traffic." },
    ],
  },

  web3: {
    label: "Web3 projects",
    seoTitle: "AI Marketing for Web3 Projects — Cited by LLMs | Axy.digital",
    seoDescription:
      "Turn ecosystem narratives and market cycles into campaigns cited by ChatGPT, Gemini, Claude & Perplexity. +62% community engagement. Start now.",
    hero: {
      title: "AI Marketing for Web3 Projects",
      tagline:
        "Turn ecosystem narratives and market cycles into campaigns cited by ChatGPT, Gemini, Claude & Perplexity — +62% community engagement in 30 days.",
      body: [
        "Web3 moves fast. Narratives shift, ecosystems rotate, protocols launch, communities form, incentives change, and attention moves before traditional marketing cycles can react.",
        "Axy.digital helps Web3 teams detect market signals and turn them into timely campaigns across search, AI discovery, social, and community channels.",
        "Stay visible in a market where attention moves by the hour.",
      ],
      cta: "Build your Web3 marketing engine",
    },
    problem: {
      heading: "Web3 marketing is too fast for manual content calendars",
      body: [
        "In Web3, relevance is tied to timing. A funding announcement, ecosystem upgrade, regulatory change, market cycle, airdrop, integration, or technical debate can create a short window of attention. But most teams are stuck choosing between:",
      ],
      bullets: [
        "Founder-led posting",
        "Community updates",
        "Agency retainers",
        "Generic content",
        "Fragmented analytics",
        "Manual research",
        "Reactive campaigns",
      ],
      closing: "By the time content goes live, the market may have moved to the next narrative.",
    },
    approach: {
      heading: "Turn ecosystem signals into GTM execution",
      body: [
        "Axy.digital tracks what the market is talking about, what users are searching, what competitors are publishing, and where ecosystem attention is moving. Then it helps generate:",
      ],
      bullets: [
        "Ecosystem thought leadership",
        "Technical education content",
        "GEO and SEO articles",
        "LinkedIn and X posts",
        "Founder campaigns",
        "Competitor positioning content",
        "Community education content",
        "AI visibility reports",
      ],
    },
    helps: {
      heading: "What Axy.digital helps Web3 projects do",
      items: [
        { title: "Own emerging narratives", body: "Respond to ecosystem shifts, regulatory developments, technical debates, and market cycles while they are still relevant." },
        { title: "Improve AI and search discovery", body: "Help your project show up when users, developers, investors, and partners ask AI tools about your category." },
        { title: "Translate technical complexity", body: "Turn protocol design, infrastructure, token mechanics, or product updates into content people can actually understand." },
        { title: "Support community and ecosystem growth", body: "Create campaigns that educate, activate, and build trust with your target audience." },
        { title: "Reduce agency dependency", body: "Move faster without relying on agencies that do not understand your category deeply enough." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, Web3 projects can:",
      bullets: [
        "Build narrative authority",
        "Capture ecosystem attention faster",
        "Improve visibility across AI and search",
        "Turn technical updates into market-facing content",
        "Support founder and community-led growth",
        "Respond faster to competitors and market cycles",
      ],
    },
    closing: {
      heading: "Turn market cycles into momentum",
      body: "Axy.digital helps Web3 projects detect what the ecosystem cares about and turn it into campaigns before attention moves on.",
      cta: "Build your Web3 marketing engine",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for Web3 projects?", answer: "It tracks ecosystem narratives, competitor moves, and market cycles — then turns them into GEO articles, X threads, founder posts, and technical education content so your project shows up while attention is still on the narrative." },
      { question: "How is this different from hiring a Web3 marketing agency or freelancer?", answer: "Axy.digital runs the full loop autonomously — research, drafting, publishing, distribution — at the speed the market actually moves, versus agencies that ship weeks after a narrative peaks." },
      { question: "Does it handle competitor comparisons and category positioning content?", answer: "Yes. It generates comparison content and competitor-response campaigns whenever a rival protocol or ecosystem player makes a move." },
      { question: "Can it handle X threads and community content, not just SEO blogs?", answer: "Yes. X threads, founder posts, and community education content are core outputs alongside GEO/SEO articles — matching where Web3 attention actually lives." },
      { question: "How quickly do we see results?", answer: "Narrative-led content ships within hours of a signal, and AI-citation plus community engagement typically lift within the first 30–90 days." },
      { question: "Does this replace our community or founder-led marketing?", answer: "No. Axy.digital handles operational content ops so founders and community leads can focus on ecosystem relationships, launches, and community-building." },
      { question: "Does it track whether we're cited or recommended by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting shows whether your project is mentioned across ChatGPT, Gemini, Claude, and Perplexity when users, developers, and investors ask about your category." },
    ],
  },

  fitness: {
    label: "Fitness & wellness brands",
    seoTitle: "AI Search Visibility for Fitness & Wellness Brands | Axy.digital",
    seoDescription:
      "Get cited by ChatGPT, Gemini, Claude & Google when customers ask for supplements, routines, and brands they can trust. +48% social reach. Start now.",
    hero: {
      title: "AI Search Visibility for Fitness & Wellness Brands",
      tagline:
        "Get cited by ChatGPT, Gemini, Claude & Google when customers ask what to eat, take, or trust — +48% social reach month over month.",
      body: [
        "Fitness and wellness customers are overwhelmed with advice. They search, compare, ask AI tools, follow creators, read reviews, and look for brands they can trust.",
        "Axy.digital helps fitness and wellness brands detect what customers care about right now and turn those signals into campaigns across AI search, Google, LinkedIn, social, and content.",
        "Build trust where your customers are already looking for answers.",
      ],
      cta: "Get your AI visibility report",
    },
    problem: {
      heading: "Wellness marketing is noisy, but trust still wins",
      body: [
        "The fitness and wellness market is full of trends, claims, products, creators, routines, and conflicting advice. Customers are asking:",
      ],
      bullets: [
        "“Best supplement for energy”",
        "“How to recover after intense training”",
        "“Is creatine good for women?”",
        "“Best wellness brands for busy professionals”",
        "“How to improve sleep naturally”",
        "“What should I eat before a race?”",
      ],
      closing:
        "If your brand is not part of those answers, customers may find a competitor before they ever discover you.",
    },
    approach: {
      heading: "Turn customer curiosity into campaigns",
      body: [
        "Axy.digital tracks customer questions, wellness trends, search demand, competitor content, and social conversations. Then it generates campaigns designed to build authority and capture demand.",
      ],
      bullets: [
        "Educational articles",
        "Product-led content",
        "Social campaigns",
        "Founder or expert thought leadership",
        "AI visibility reports",
        "Search trend insights",
        "Category explainers",
        "Comparison content",
      ],
    },
    helps: {
      heading: "What Axy.digital helps fitness and wellness brands do",
      items: [
        { title: "Build trust through education", body: "Create content that answers real customer questions with clarity and credibility." },
        { title: "Capture search and AI demand", body: "Show up when customers ask Google or AI tools for guidance, recommendations, and product comparisons." },
        { title: "Respond to trends without chasing every fad", body: "Identify which trends are worth acting on and turn them into brand-aligned campaigns." },
        { title: "Differentiate in crowded categories", body: "Use your philosophy, ingredients, method, community, or product quality to stand out." },
        { title: "Keep content consistent", body: "Generate campaigns regularly without relying on constant manual ideation." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, fitness and wellness brands can:",
      bullets: [
        "Build stronger customer trust",
        "Capture high-intent search demand",
        "Improve visibility across AI discovery",
        "Turn trends into timely campaigns",
        "Educate customers at scale",
        "Stay consistent across content and social",
      ],
    },
    closing: {
      heading: "Become the brand customers trust before they buy",
      body: "Axy.digital helps fitness and wellness brands turn customer questions into campaigns that build authority and demand.",
      cta: "Get your AI visibility report",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for fitness and wellness brands?", answer: "It maps the questions your customers actually ask — supplement comparisons, recovery routines, ingredient safety, brand recommendations — and turns them into educational articles, product-led content, and social campaigns so your brand shows up across Google and AI answers." },
      { question: "How is this different from hiring a wellness content agency or freelancer?", answer: "Axy.digital runs the full loop autonomously — research, drafting, publishing, distribution — at a fraction of agency time and cost, and keeps learning from what performs across your categories." },
      { question: "Does it handle competitor comparisons and category-explainer content?", answer: "Yes. It generates comparison and category-explainer content so your brand shows up when customers evaluate you against alternatives." },
      { question: "Can it handle Instagram, TikTok, and creator-style social content, not just blog articles?", answer: "Yes. Social campaigns, expert/founder thought leadership, and educational content are core outputs alongside long-form SEO/GEO articles." },
      { question: "How quickly do we see results?", answer: "Brands typically see AI-citation lift and social reach compound within the first 60–90 days as content stacks up across search, LLMs, and social." },
      { question: "Does this replace our marketing team?", answer: "No. Axy.digital handles the operational execution so your team can focus on brand, community, and creative direction instead of content ops." },
      { question: "Does it track whether we're cited or recommended by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting shows whether your brand is mentioned across ChatGPT, Gemini, Claude, and Perplexity when customers ask for product and routine recommendations." },
    ],
  },

  ecommerce: {
    label: "Ecommerce & luxury brands",
    seoTitle: "AI Search Visibility for Ecommerce & Luxury Brands | Axy.digital",
    seoDescription:
      "Get cited by ChatGPT, Gemini, Claude & Google for product and category searches — 3.2x ROAS on narrative-aware social. Start now.",
    hero: {
      title: "AI Search Visibility for Ecommerce & Luxury Brands",
      tagline:
        "Get cited by ChatGPT, Gemini, Claude & Google when shoppers ask for products worth buying — 3.2x ROAS on narrative-aware social.",
      body: [
        "Modern shoppers discover brands across search, AI tools, social platforms, creators, newsletters, marketplaces, and cultural conversations.",
        "Axy.digital helps ecommerce and luxury brands detect what customers want, understand where attention is moving, and turn those signals into campaigns across AI search, SEO, social, and content.",
        "Create demand with more relevance, not more noise.",
      ],
      cta: "Build your AI marketing engine",
    },
    problem: {
      heading: "Brand discovery is fragmented",
      body: [
        "Customers no longer move neatly from ad to website to purchase. They search for recommendations, compare products, ask AI tools, follow creators, read editorial content, and discover brands through social proof. For ecommerce and luxury brands, this creates a challenge:",
      ],
      bullets: [
        "Paid ads are more expensive",
        "Organic reach is harder to predict",
        "Trends move quickly",
        "Brand storytelling needs consistency",
        "Search behavior is changing",
        "Generic AI content damages brand perception",
      ],
      closing: "More content is not enough. The right content matters.",
    },
    approach: {
      heading: "Turn market signals into brand-relevant campaigns",
      body: [
        "Axy.digital tracks customer search behavior, cultural trends, competitor movement, social signals, product demand, and AI visibility. Then it generates campaigns that align with your brand positioning and market opportunity.",
      ],
      bullets: [
        "SEO and GEO articles",
        "Product education content",
        "Collection launch campaigns",
        "Founder or creative director thought leadership",
        "Social posts",
        "Trend-led campaigns",
        "Category explainers",
        "AI visibility reports",
      ],
    },
    helps: {
      heading: "What Axy.digital helps ecommerce and luxury brands do",
      items: [
        { title: "Capture product and category demand", body: "Create content around what shoppers are actively searching, comparing, and asking." },
        { title: "Build desire through relevance", body: "Turn cultural signals and customer intent into campaigns that feel timely and brand-aligned." },
        { title: "Improve AI search visibility", body: "Track whether your brand appears when customers ask AI tools for product recommendations." },
        { title: "Support launches and collections", body: "Generate campaigns around new products, seasonal moments, cultural trends, and customer needs." },
        { title: "Protect brand quality", body: "Use structured brand context so AI-generated content stays aligned with your voice and positioning." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, ecommerce and luxury brands can:",
      bullets: [
        "Improve organic discovery",
        "Reduce reliance on paid ads",
        "Build stronger category authority",
        "Turn trends into brand-aligned campaigns",
        "Support product launches",
        "Show up across AI and search discovery",
      ],
    },
    closing: {
      heading: "Turn customer intent into brand momentum",
      body: "Axy.digital helps ecommerce and luxury brands create campaigns that capture demand, build desire, and improve discovery.",
      cta: "Build your AI marketing engine",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for ecommerce and luxury brands?", answer: "It tracks shopper search behavior, cultural trends, and competitor moves — then turns them into GEO articles, PDP-adjacent content, category explainers, and launch campaigns so your brand shows up across Google, AI answers, and social." },
      { question: "How is this different from hiring an ecommerce content agency or freelancer?", answer: "Axy.digital runs the full loop autonomously and continuously across every category and collection, at a fraction of agency time and cost, while keeping outputs brand-aligned." },
      { question: "Does it handle competitor comparisons and category-explainer content?", answer: "Yes. It generates comparison content and category positioning that helps your brand show up when shoppers evaluate alternatives." },
      { question: "Can it handle Instagram, TikTok, and editorial-style social content, not just SEO blogs?", answer: "Yes. Social posts, creative-director thought leadership, and trend-led campaigns are core outputs alongside long-form SEO/GEO content." },
      { question: "How quickly do we see results?", answer: "Brands typically see AI-citation lift and organic discovery compound within the first 60–90 days, with lower dependency on paid acquisition as content stacks." },
      { question: "Does this replace our brand or creative team?", answer: "No. Axy.digital handles the operational execution so your team can focus on brand direction, creative, and merchandising instead of content ops." },
      { question: "Does it track whether we're cited or recommended by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting shows whether your brand and products are mentioned across ChatGPT, Gemini, Claude, and Perplexity when shoppers ask for recommendations." },
    ],
  },

  logistics: {
    label: "Logistics",
    seoTitle: "AI Search Visibility for Logistics Companies | Axy.digital",
    seoDescription:
      "Turn supply chain shifts and regulatory changes into campaigns cited by ChatGPT, Gemini & Google — +33% RFP invites in 6 months. Get started.",
    hero: {
      title: "AI Search Visibility for Logistics Companies",
      tagline:
        "Show up when buyers search for a more reliable partner across Google, ChatGPT, Gemini & Perplexity — +33% RFP invites in the first 6 months.",
      body: [
        "Logistics buyers care about reliability, speed, compliance, cost, and operational resilience. But the market is constantly shifting: tariffs, regulations, shipping disruptions, fuel costs, regional demand, and supply chain risk all influence buying decisions.",
        "Axy.digital helps logistics companies detect those shifts and turn them into timely campaigns across search, AI discovery, LinkedIn, and industry content.",
        "Show up when customers are actively looking for a more reliable partner.",
      ],
      cta: "Get your logistics AI visibility report",
    },
    problem: {
      heading: "Logistics buyers search when something changes",
      body: ["Companies often look for new logistics partners when they face pressure:"],
      bullets: [
        "A supply chain disruption",
        "A new regulation",
        "A regional expansion",
        "Rising costs",
        "Poor service from an incumbent",
        "A need for faster delivery",
        "A new ecommerce or distribution requirement",
      ],
      closing:
        "These moments create high-intent demand. But many logistics companies still market through static service pages, sales outreach, referrals, and occasional updates. That means they miss the exact windows when buyers are searching for solutions.",
    },
    approach: {
      heading: "Turn supply chain signals into demand capture",
      body: [
        "Axy.digital tracks market shifts, customer search behavior, competitor content, regulatory updates, regional demand, and industry narratives. Then it turns those signals into:",
      ],
      bullets: [
        "SEO and GEO articles",
        "LinkedIn campaigns",
        "Industry commentary",
        "Service-page recommendations",
        "AI visibility reports",
        "Competitor visibility insights",
        "Buyer education content",
      ],
    },
    helps: {
      heading: "What Axy.digital helps logistics companies do",
      items: [
        { title: "Capture high-intent demand", body: "Create content around the problems buyers are actively searching for, from supply chain resilience to last-mile delivery." },
        { title: "Build trust through expertise", body: "Turn operational knowledge into content that helps buyers understand risk, cost, compliance, and reliability." },
        { title: "Improve AI search visibility", body: "Track whether your company appears when buyers ask AI systems for logistics providers, freight partners, or supply chain solutions." },
        { title: "Respond to market disruptions", body: "Use regulatory changes, shipping delays, tariffs, and industry shifts as timely campaign triggers." },
        { title: "Support sales conversations", body: "Create content that educates prospects before they speak to your team." },
      ],
    },
    outcomes: {
      intro: "With Axy.digital, logistics companies can:",
      bullets: [
        "Improve visibility with high-intent buyers",
        "Build authority around supply chain expertise",
        "Capture demand from market disruptions",
        "Support sales with better educational content",
        "Differentiate from commodity providers",
        "Show up across search and AI discovery",
      ],
    },
    closing: {
      heading: "Become the logistics partner buyers find when reliability matters",
      body: "Axy.digital helps logistics companies turn supply chain change into timely campaigns that build trust and drive demand.",
      cta: "Get your logistics AI visibility report",
    },
    caseStudySlugs: SHARED_CASE_STUDIES,
    faq: [
      { question: "What does Axy.digital do for logistics companies?", answer: "It tracks supply chain shifts, regulatory changes, and buyer search behavior — then turns them into SEO/GEO articles, lane briefs, LinkedIn commentary, and service-page content so your company shows up when shippers are actively looking for a new partner." },
      { question: "How is this different from hiring an industry marketing agency or freelancer?", answer: "Axy.digital runs the full loop autonomously and continuously across every lane, service, and region, at a fraction of agency time and cost — and it moves at the speed of disruption, not the speed of retainers." },
      { question: "Does it handle competitor comparisons and service-line positioning?", answer: "Yes. It generates comparison content and service-line positioning so your company shows up when buyers evaluate you against incumbents and alternatives." },
      { question: "Can it handle LinkedIn commentary and industry thought leadership, not just service pages?", answer: "Yes. LinkedIn campaigns, founder/executive commentary on regulation and disruption, and industry education content are core outputs alongside SEO/GEO articles." },
      { question: "How quickly do we see results?", answer: "Signal-led content ships within days of a disruption, and RFP invites and AI-citation typically lift within the first 90–180 days as content compounds." },
      { question: "Does this replace our sales or BD team?", answer: "No. Axy.digital handles the operational execution so your team can focus on RFPs, customer conversations, and closing — not writing service pages." },
      { question: "Does it track whether we're cited or recommended by ChatGPT and other AI tools?", answer: "Yes. AI visibility reporting shows whether your company is mentioned across ChatGPT, Gemini, Claude, and Perplexity when buyers ask for logistics providers, freight partners, or supply chain solutions." },
    ],
  },
};
